import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrganizationVendors, usePrimaryOrganization } from "../../../hooks";
import { useOrgDataSources } from "../../../hooks/useOrganizationVendors";
import {
  useEmploymentCollectionGroups,
  useNonEmploymentCollectionGroups,
} from "../../../hooks/useCollectionGroups";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { useSnackBar } from "@truevault/aquilo";
import { Api } from "../../../../common/service/Api";
import {
  CollectionContext,
  OrganizationVendorSettingsDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UpdateVendorArgs } from "../components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../get-compliant/data-recipients/shared";
import { SHOPIFY_SNI_SLUG } from "../../../surveys/sellingSurvey";
import { VendorDetailsModal } from "./VendorDetailsModal";
import { RemoveVendorConfirmationModal } from "./RemoveVendorConfirmationModal";
import {
  buildVendorInventoryItems,
  filterInventoryDataSources,
  isVendorRemovable,
  removeVendorCompletely,
  vendorRemovalRequiresPageReload,
} from "./vendorInventoryUtils";

/**
 * Self-contained vendor settings modal for pages outside the Vendor List — e.g. the Data Flows
 * disclosures step opens vendor settings in place instead of navigating away (crit c_98a5de).
 *
 * The Vendor List wires VendorDetailsModal to its own optimistic local state; here every mutation
 * goes straight to the API and then invalidates the shared react-query caches, so both this modal
 * and the host page re-read fresh data.
 */
export function StandaloneVendorSettingsModal({
  vendorId,
  pageContext,
  onClose,
  onChanged,
}: {
  vendorId: string;
  pageContext: CollectionContext;
  onClose: () => void;
  /** Called after any change so the host page can refresh what it derives from vendor data.
   *  Receives the vendor id when the change was a removal. */
  onChanged?: (removedVendorId?: string) => void;
}) {
  const [org] = usePrimaryOrganization();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackBar();
  const collectionContext = useMemo(() => [pageContext], [pageContext]);
  const includeEmptyContext = pageContext === "CONSUMER";

  // Deliberately unfiltered by context: an edit made in this modal can subtract the vendor's
  // collection context (turning off "Recipient" moves it to the page's Inactive section), and a
  // context-filtered query would stop resolving the very vendor being edited — the settings
  // button then looked dead and threw "no longer available" toasts (POL-7818). The context
  // shaping still happens in buildVendorInventoryItems below.
  //
  // Not `onlyUseSettled`: that hides the cached list until this mount's own refetch completes,
  // while `vendorsRequest.success` is already true from the cache. On any connection where the
  // refetch outlasts the grace period below the modal concluded the vendor was gone, toasted and
  // closed — the dead button QA kept reproducing on staging (POL-7818 #1/#2.5). Render from the
  // cache and use `vendorsSettled` to decide when "not found" is the server's answer.
  const [vendors, vendorsRequest, refreshVendors, , vendorsSettled] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ true,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ false,
  );
  const [dataSources, dataSourcesRequest, refreshDataSources] = useOrgDataSources(org?.id);

  const {
    setAnswer: setSellingAndSharingAnswer,
    refresh: refreshSellingAndSharingAnswers,
    request: sellingAndSharingRequest,
  } = useSurvey(org?.id, SELLING_AND_SHARING_SURVEY_NAME);

  // useCollectionGroups has no "only use settled" behavior, so track the requests: these gate the
  // modal's Data Collection tab and would otherwise make it appear a beat after the modal opens.
  const [nonEmploymentCollectionGroups, nonEmploymentGroupsRequest] =
    useNonEmploymentCollectionGroups(org?.id);
  const [employmentCollectionGroups, employmentGroupsRequest] = useEmploymentCollectionGroups(
    org?.id,
  );
  const collectionGroupsForContext =
    pageContext === "EMPLOYEE" ? employmentCollectionGroups : nonEmploymentCollectionGroups;
  const hasAnyCollectedPics = collectionGroupsForContext?.some((group) => group.inUse) ?? false;

  const [isDisabledWhileLoading, setIsDisabledWhileLoading] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  // Like the Vendor List's local state, a save shows here at once, so a second save made while one
  // is in flight (e.g. Data Selling during a background sharing save) spreads the new values rather
  // than stale props. Dropped once a refetch lands with no save in flight.
  const [optimisticSettings, setOptimisticSettings] =
    useState<OrganizationVendorSettingsDto | null>(null);
  const savesInFlightRef = useRef(0);
  useEffect(() => {
    if (savesInFlightRef.current === 0) {
      setOptimisticSettings(null);
    }
  }, [vendors]);

  const allVendors = useMemo(() => {
    if (!vendors || !dataSources) return [];
    return buildVendorInventoryItems({
      recipients: optimisticSettings
        ? vendors.map((v) => {
            if (v.vendorId !== vendorId) return v;
            const updated = { ...v, ...optimisticSettings };
            const classification = v.classificationOptions?.find(
              (option) => option.id === optimisticSettings.classificationId,
            );
            return classification ? { ...updated, classification } : updated;
          })
        : vendors,
      dataSources: filterInventoryDataSources(dataSources, pageContext, includeEmptyContext),
      collectionContext: pageContext,
      includeEmptyContext,
      // The host page already decided this vendor belongs here — keep resolving it even when an
      // edit leaves it without this page's collection context (POL-7818).
      alwaysIncludeVendorIds: new Set([vendorId]),
    });
  }, [vendors, optimisticSettings, dataSources, pageContext, includeEmptyContext, vendorId]);

  const inventoryVendor = allVendors.find((v) => v.vendorId === vendorId);
  const existingCategories = useMemo(
    () => new Set(allVendors.map((v) => v.category).filter((x): x is string => Boolean(x))),
    [allVendors],
  );

  const refreshAfterChange = async (removedVendorId?: string) => {
    refreshVendors();
    refreshDataSources();
    // The host page (e.g. disclosures) derives from these too.
    void queryClient.invalidateQueries({ queryKey: ["organizationVendors", org?.id] });
    void queryClient.invalidateQueries({ queryKey: ["dataMap", org?.id] });
    onChanged?.(removedVendorId);
  };

  const updateVendor = async (updateVendorArgs: UpdateVendorArgs) => {
    // Convert the classification DTO to its id when only the DTO was provided (same as the
    // Vendor List's updateVendor).
    if (
      !updateVendorArgs?.vendorSettings?.classificationId &&
      "classification" in updateVendorArgs.vendorSettings
    ) {
      updateVendorArgs.vendorSettings.classificationId =
        // @ts-ignore - if the classification property exists it's a DTO object with an id
        updateVendorArgs.vendorSettings.classification?.id;
    }
    const isThisVendor = updateVendorArgs.vendorId === vendorId;
    if (isThisVendor) {
      savesInFlightRef.current += 1;
      setOptimisticSettings((prev) => ({ ...prev, ...updateVendorArgs.vendorSettings }));
    }
    try {
      await Api.organizationVendor.addOrUpdateVendor(
        org?.id,
        updateVendorArgs.vendorId,
        updateVendorArgs.vendorSettings,
      );
    } catch (e) {
      if (isThisVendor) {
        setOptimisticSettings(null);
      }
      // Same as the Vendor List: the host reports a failed save, callers only roll back.
      showSnackbar("Couldn't save your changes. Please try again.", { variant: "error" });
      throw e;
    } finally {
      if (isThisVendor) {
        savesInFlightRef.current -= 1;
      }
    }
    await refreshAfterChange();
  };

  const removeDataSource = async (targetVendorId: string) => {
    // Same as the Vendor List: the last context deletes the source and its received categories.
    await Api.orgDataSource.removeOrgDataSource(org?.id, targetVendorId, collectionContext);
    await refreshAfterChange();
  };
  const removeRecipientOnly = async (targetVendorId: string) => {
    // Same as the Vendor List: the last context deletes the recipient row, the source stays.
    await Api.organizationVendor.removeVendor(
      org?.id,
      targetVendorId,
      collectionContext,
      /* deleteOnEmpty = */ true,
      /* removeFromDataMap = */ false,
      /* keepDataSource = */ true,
    );
    await refreshAfterChange();
  };

  const setGoogleAnalyticsDataSharingAnswer = async (newAnswer: boolean) => {
    await setSellingAndSharingAnswer("google-analytics-data-sharing", newAnswer ? "true" : "false");
    await refreshSellingAndSharingAnswers();
    await refreshAfterChange();
  };
  const setShopifySNIAnswer = async (newAnswer: boolean) => {
    await setSellingAndSharingAnswer(SHOPIFY_SNI_SLUG, newAnswer ? "true" : "false");
    // Changing the SNI answer adds/removes the SNI vendor — a full reload is the reliable refresh,
    // matching the Vendor List behavior.
    window.location.reload();
  };

  const handleConfirmedRemove = async () => {
    setIsRemoving(true);
    try {
      await removeVendorCompletely(org?.id, vendorId);
      setConfirmRemoveOpen(false);
      if (vendorRemovalRequiresPageReload(inventoryVendor?.vendorKey)) {
        window.location.reload();
        return;
      }
      await refreshAfterChange(vendorId);
      onClose();
    } catch (e) {
      // Leave the confirmation open so the user can retry.
      setIsRemoving(false);
      showSnackbar(`Couldn't remove ${inventoryVendor?.name ?? "this vendor"}. Please try again.`, {
        variant: "error",
      });
    }
  };

  const isReady = Boolean(
    org &&
      vendorsRequest.success &&
      dataSourcesRequest.success &&
      sellingAndSharingRequest.success &&
      nonEmploymentGroupsRequest.success &&
      employmentGroupsRequest.success,
  );

  // The vendor is pinned into `allVendors` regardless of collection context, so it can only be
  // missing when it truly left the org — removed in another tab, or a stale deep link. The modal
  // still has to close then (rendering null while the caller thinks it is open leaves its button
  // dead), but only a vendor that was never here is worth an error. A cached list that predates
  // the vendor is not evidence either: wait for this mount's fetch to settle before concluding.
  const hasSeenVendor = useRef(false);
  if (inventoryVendor) {
    hasSeenVendor.current = true;
  }
  const hasHandledMissingVendor = useRef(false);
  const isVendorGone = isReady && vendorsSettled && !inventoryVendor;
  const wasVendorNeverFound = isVendorGone && !hasSeenVendor.current;

  // Deliberately deferred: the vendor list is re-derived as its queries settle and re-filter, so
  // "not found" flickers true for a frame on a perfectly healthy vendor. Closing on that flicker
  // unmounted the modal mid-render and left the caller's button dead. Only a state that survives
  // the tick is real, and closing from a timeout keeps the unmount out of React's render pass.
  // Held in refs so the effect below depends only on the two state values. Callers pass an
  // inline `onClose`, whose identity changes on every parent render — as a dependency it re-ran
  // the effect, cleared the pending timer and armed a fresh one, so a page that re-renders more
  // often than every 500ms (a React Query refetch is enough) never let the timeout fire.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  useEffect(() => {
    if (!isVendorGone || hasHandledMissingVendor.current) {
      return;
    }
    const timer = window.setTimeout(() => {
      hasHandledMissingVendor.current = true;
      if (wasVendorNeverFound) {
        showSnackbarRef.current("That vendor is no longer available.", { variant: "error" });
      }
      onCloseRef.current();
    }, 500);
    return () => window.clearTimeout(timer);
  }, [isVendorGone, wasVendorNeverFound]);

  // Keep quiet while loading — the modal appears as soon as the (usually cached) queries settle.
  if (!isReady || !inventoryVendor) {
    return null;
  }

  return (
    <>
      {/* Hidden, not stacked, while the removal confirmation is up: a second dialog opened over
          Vendor Settings counted as an outside click on it, so Cancel closed both. Cancel now just
          brings Vendor Settings back, as on the Vendor List. */}
      {!confirmRemoveOpen && (
        <VendorDetailsModal
          inventoryVendor={inventoryVendor}
          org={org}
          allVendors={allVendors}
          hasAnyCollectedPics={hasAnyCollectedPics}
          existingCategories={existingCategories}
          updateVendor={updateVendor}
          removeDataSource={removeDataSource}
          removeRecipientOnly={removeRecipientOnly}
          isDisabledWhileLoading={isDisabledWhileLoading}
          setIsDisabledWhileLoading={setIsDisabledWhileLoading}
          setGoogleAnalyticsDataSharingAnswer={setGoogleAnalyticsDataSharingAnswer}
          setShopifySNIAnswer={setShopifySNIAnswer}
          onClose={onClose}
          onNext={onClose}
          onDelete={() => setConfirmRemoveOpen(true)}
          deleteDisabled={!isVendorRemovable(inventoryVendor.vendorKey)}
          categoryGroupMode={pageContext === "EMPLOYEE" ? "hr" : "consumer"}
          pageContext={pageContext}
        />
      )}
      {confirmRemoveOpen && (
        <RemoveVendorConfirmationModal
          vendorName={inventoryVendor.name}
          onConfirm={() => {
            if (!isRemoving) {
              void handleConfirmedRemove();
            }
          }}
          onCancel={() => setConfirmRemoveOpen(false)}
          onBack={() => setConfirmRemoveOpen(false)}
        />
      )}
    </>
  );
}
